import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useSiteLanguage } from "@/contexts/LanguageContext";
import { localizeCategories, localizeProducts } from "@/lib/catalogLocalization";
import { localizePublicSettings } from "@/lib/publicPageLocalization";

const STATIC_PUBLIC_DATA = {
  settings: "/data/settings.json",
  categories: "/data/categories.json",
  products: "/data/products.json",
  certificates: "/data/certificates.json",
};

async function fetchStaticPublicData(key, fallbackValue) {
  const target = STATIC_PUBLIC_DATA[key];
  if (!target) return fallbackValue;

  try {
    const response = await fetch(target, { cache: "no-store" });
    if (!response.ok) return fallbackValue;
    const data = await response.json();
    return data ?? fallbackValue;
  } catch {
    return fallbackValue;
  }
}

function filterStaticProducts(items, path) {
  const [, queryString = ""] = path.split("?");
  const params = new URLSearchParams(queryString);
  let next = Array.isArray(items) ? [...items] : [];

  const category = params.get("category");
  if (category) {
    next = next.filter((item) => item.category_slug === category);
  }

  if (params.get("featured") === "true") {
    next = next.filter((item) => Boolean(item.featured));
  }

  return next;
}

async function safePublicGet(path, fallbackValue) {
  try {
    const { data } = await api.get(path);
    return data ?? fallbackValue;
  } catch {
    if (path.startsWith("/settings")) {
      return fetchStaticPublicData("settings", fallbackValue);
    }
    if (path.startsWith("/categories")) {
      return fetchStaticPublicData("categories", fallbackValue);
    }
    if (path.startsWith("/certificates")) {
      return fetchStaticPublicData("certificates", fallbackValue);
    }
    if (path.startsWith("/products")) {
      const products = await fetchStaticPublicData("products", []);
      return filterStaticProducts(products, path);
    }
    return fallbackValue;
  }
}

export async function fetchPublicProductBySlug(slug) {
  try {
    const { data } = await api.get(`/products/${slug}`);
    return data ?? null;
  } catch {
    const products = await fetchStaticPublicData("products", []);
    return products.find((item) => item.slug === slug || item.id === slug) || null;
  }
}

export function usePublicSettings() {
  const { language } = useSiteLanguage();
  const query = useQuery({
    queryKey: ["public-settings"],
    queryFn: async () => safePublicGet("/settings", {}),
    staleTime: 5 * 60_000,
    retry: false,
  });

  return {
    ...query,
    data: useMemo(() => localizePublicSettings(query.data || {}, language), [query.data, language]),
  };
}

export function usePublicCategories() {
  const { language } = useSiteLanguage();
  const query = useQuery({
    queryKey: ["public-categories"],
    queryFn: async () => safePublicGet("/categories", []),
    staleTime: 5 * 60_000,
    retry: false,
  });

  return {
    ...query,
    data: useMemo(() => localizeCategories(query.data || [], language), [query.data, language]),
  };
}

export function usePublicCertificates() {
  return useQuery({
    queryKey: ["public-certificates"],
    queryFn: async () => safePublicGet("/certificates", []),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function usePublicFeaturedProducts() {
  const { language } = useSiteLanguage();
  const query = useQuery({
    queryKey: ["featured-products"],
    queryFn: async () => safePublicGet("/products?featured=true", []),
    staleTime: 5 * 60_000,
    retry: false,
  });

  return {
    ...query,
    data: useMemo(() => localizeProducts(query.data || [], language), [query.data, language]),
  };
}

export function usePublicProducts(queryKey, path) {
  const { language } = useSiteLanguage();
  const query = useQuery({
    queryKey,
    queryFn: async () => safePublicGet(path, []),
    staleTime: 60_000,
    retry: false,
  });

  return {
    ...query,
    data: useMemo(() => localizeProducts(query.data || [], language), [query.data, language]),
  };
}
